import { useTranslation } from "react-i18next";
import DataTable, {
  DataTableColumn,
  DataTableRow,
} from "../../../components/carbon/DataTable/DataTable";
import EmptyState from "../../../components/carbon/EmptyState/EmptyState";
import { CompanyProfileTabProps } from "./OverviewTab";

const NONE = "-";

interface MainClientRow extends DataTableRow {
  clientName: string;
  contactPerson: string;
  email: string;
  phone: string;
}

/**
 * The "Main Clients" tab — now a Carbon DataTable (client, contact person, email, phone) instead
 * of a card list. The contact fields were already on the record but were never shown before. The
 * empty state gates on `mainClients` (not `files`, which was the pre-Carbon copy/paste bug).
 */
const MainClientsTab = ({ companyProfile }: CompanyProfileTabProps) => {
  const { t } = useTranslation();
  const mainClients = companyProfile.mainClients ?? [];

  if (mainClients.length === 0) {
    return (
      <div className="company-profile__empty-section">
        <h3>{t("companyProfile.noMainClientsAdded")}</h3>
        <p>{t("companyProfile.addMainClientsToProfile")}</p>
      </div>
    );
  }

  const rows: MainClientRow[] = mainClients.map((client, index) => ({
    id: client.id ?? index,
    clientName: client.clientName || NONE,
    contactPerson: client.contactPersonName || NONE,
    email: client.contactEmail || NONE,
    phone: client.contactPhone || NONE,
  }));

  const headers: DataTableColumn<MainClientRow>[] = [
    { key: "clientName", header: t("companyProfile.table.client") },
    { key: "contactPerson", header: t("companyProfile.table.contactPerson") },
    { key: "email", header: t("companyProfile.email") },
    { key: "phone", header: t("companyProfile.phone") },
  ];

  return (
    <div className="company-profile__section">
      <h2 className="company-profile__section-heading">
        {t("companyProfile.mainClients")}
      </h2>
      <DataTable
        headers={headers}
        rows={rows}
        isLoading={false}
        empty={<EmptyState title={t("companyProfile.noMainClientsAdded")} />}
      />
    </div>
  );
};

export default MainClientsTab;
